import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const dziTypes = [
  {
    name: "九眼天珠",
    desc: "象徵至尊能量，寓意財富、智慧與貴人相助，為天珠中最具代表性的守護之珠。",
    tag: "至尊守護",
    img: "https://readdy.ai/api/search-image?query=nine%20eye%20Tibetan%20dzi%20bead%20ancient%20agate%20stone%20with%20nine%20eye%20pattern%20close%20up%20macro%20photography%20on%20white%20marble%20background%20luxury%20jewelry%20product%20shot%20warm%20golden%20light%20high%20detail%20texture&width=400&height=400&seq=dzi001&orientation=squarish",
  },
  {
    name: "三眼天珠",
    desc: "代表福、祿、壽三大能量，象徵事業、財富與健康，是最受歡迎的吉祥天珠之一。",
    tag: "福祿壽",
    img: "https://readdy.ai/api/search-image?query=three%20eye%20Tibetan%20dzi%20bead%20ancient%20agate%20stone%20with%20three%20eye%20pattern%20close%20up%20macro%20photography%20on%20cream%20white%20background%20luxury%20jewelry%20product%20shot%20soft%20warm%20light%20high%20detail&width=400&height=400&seq=dzi002&orientation=squarish",
  },
  {
    name: "虎牙天珠",
    desc: "象徵勇氣與守護，被認為具有化解負能量的寓意，為勇者的護身符。",
    tag: "勇氣守護",
    img: "https://readdy.ai/api/search-image?query=tiger%20tooth%20Tibetan%20dzi%20bead%20ancient%20agate%20stone%20with%20tiger%20tooth%20pattern%20close%20up%20macro%20photography%20on%20light%20stone%20background%20luxury%20jewelry%20product%20shot%20natural%20light%20high%20detail%20texture&width=400&height=400&seq=dzi003&orientation=squarish",
  },
];

export default function DziBeads() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const navigate = useNavigate();
  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="products" className="py-28 md:py-36 bg-[#f5f0e8]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-10 h-px bg-[#8b6f47]"></span>
            <span
              className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              TIBETAN DZI BEADS
            </span>
            <span className="w-10 h-px bg-[#8b6f47]"></span>
          </div>
          <h2
            className="text-[#2d2520] font-bold mb-5 leading-[1.4]"
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            西藏天珠｜千年傳承的守護之珠
          </h2>
          <p
            className="text-[#7a6a5a] max-w-2xl mx-auto leading-[1.75]"
            style={{
              fontSize: "1.1875rem",
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            天珠被視為藏文化中具神聖意義的護符之一，歷經千年流傳，象徵守護、智慧、祝福與能量平衡。
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
          {dziTypes.map((item, i) => (
            <div
              key={item.name}
              className={`bg-white rounded-2xl overflow-hidden transition-all duration-1000 cursor-pointer hover:-translate-y-1 ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Image */}
              <div className="w-full h-64 overflow-hidden">
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                />
              </div>
              {/* Content */}
              <div className="p-7">
                <div className="flex items-center justify-between mb-3">
                  <h3
                    className="text-[#2d2520] font-bold"
                    style={{
                      fontSize: "clamp(1.25rem, 2vw, 1.625rem)",
                      fontFamily: "'Noto Serif TC', serif",
                    }}
                  >
                    {item.name}
                  </h3>
                  <span
                    className="px-3 py-1 bg-[#f0e8dc] text-[#8b6f47] text-[0.875rem] rounded-full font-medium"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    {item.tag}
                  </span>
                </div>
                <p
                  className="text-[#7a6a5a] leading-[1.75]"
                  style={{
                    fontSize: "1.1875rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate("/products?category=dzi")}
            className="inline-flex items-center gap-3 px-10 py-4 bg-[#2d2520] text-white rounded-full text-[1rem] font-medium tracking-widest cursor-pointer hover:bg-[#3d3530] transition-all duration-300 whitespace-nowrap"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            探索天珠系列
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
